import React, { useState, useMemo } from 'react';

const formatHours = (decimalHours) => {
  const isNegative = decimalHours < 0;
  const absHours = Math.abs(decimalHours);
  const hours = Math.floor(absHours);
  const minutes = Math.round((absHours - hours) * 60);
  return `${isNegative ? '-' : ''}${hours}:${minutes.toString().padStart(2, '0')}h`;
};

const formatDays = (n) => `${n} ${n === 1 ? 'dag' : 'dage'}`;

export default function MobileFooter({
  totalsCurrentWeek,
  totalsCurrentWeekPeople,
  leaveDaysCurrentWeek,
  totalsYTD,
  totalsYTDPeople,
  leaveDaysYTD,
  getCustomPeriodTotals,
  getLeaveCustomPeriod,
  showProjects,
  showPeople,
  showLeave,
}) {
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  const context = showLeave ? 'leave' : showPeople ? 'people' : 'projects';

  const weekStats = useMemo(() => {
    if (context === 'leave') return { days: leaveDaysCurrentWeek };
    if (context === 'people') return totalsCurrentWeekPeople;
    return totalsCurrentWeek;
  }, [context, totalsCurrentWeek, totalsCurrentWeekPeople, leaveDaysCurrentWeek]);

  const ytdStats = useMemo(() => {
    if (context === 'leave') return { days: leaveDaysYTD };
    if (context === 'people') return totalsYTDPeople;
    return totalsYTD;
  }, [context, totalsYTD, totalsYTDPeople, leaveDaysYTD]);

  const customStats = useMemo(() => {
    if (!customStart || !customEnd || customStart > customEnd) return null;
    if (context === 'leave') return { days: getLeaveCustomPeriod(customStart, customEnd) };
    return getCustomPeriodTotals(customStart, customEnd, context);
  }, [customStart, customEnd, context, getCustomPeriodTotals, getLeaveCustomPeriod]);

  const renderVal = (s) => {
    if (!s) return <span className="text-gray-400 italic">—</span>;
    if ('days' in s) return <strong>{formatDays(s.days)}</strong>;
    const total = s.billable + s.nonBillable;
    return (
      <span>
        <strong>{formatHours(total)}</strong>
        {' '}
        <span className="text-gray-500">({formatHours(s.billable)} billable)</span>
      </span>
    );
  };

  const labelCls = 'px-2 py-0.5 bg-gray-200 text-black font-bold rounded shrink-0';

  return (
    <>
      {/* Mobile — stacked rows */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 px-3 py-2 bg-gray-50 border-t border-gray-200 z-[2000] space-y-1.5 text-xs">
        <div className="flex items-center justify-between gap-2">
          <span className={labelCls}>Nuværende uge</span>
          {renderVal(weekStats)}
        </div>
        <div className="flex items-center justify-between gap-2">
          <span className={labelCls}>År til dato</span>
          {renderVal(ytdStats)}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input
            type="date"
            value={customStart}
            onChange={e => setCustomStart(e.target.value)}
            className="border border-gray-300 rounded px-1 py-0.5 w-28 text-xs"
          />
          <span className="text-gray-400">–</span>
          <input
            type="date"
            value={customEnd}
            onChange={e => setCustomEnd(e.target.value)}
            className="border border-gray-300 rounded px-1 py-0.5 w-28 text-xs"
          />
          <div className="ml-auto">{renderVal(customStats)}</div>
        </div>
      </div>

      {/* Tablet — two rows */}
      <div className="hidden md:block lg:hidden fixed bottom-0 left-0 right-0 px-4 py-2 bg-gray-50 border-t border-gray-200 z-[2000] text-sm">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <span className={labelCls}>Nuværende uge</span>
            {renderVal(weekStats)}
          </div>
          <div className="flex items-center gap-2">
            <span className={labelCls}>År til dato</span>
            {renderVal(ytdStats)}
          </div>
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={customStart}
              onChange={e => setCustomStart(e.target.value)}
              className="border border-gray-300 rounded px-1 py-0.5 w-32 text-sm"
            />
            <span className="text-gray-400">–</span>
            <input
              type="date"
              value={customEnd}
              onChange={e => setCustomEnd(e.target.value)}
              className="border border-gray-300 rounded px-1 py-0.5 w-32 text-sm"
            />
            {renderVal(customStats)}
          </div>
        </div>
      </div>

      {/* Desktop — single row */}
      <div className="hidden lg:block fixed bottom-0 left-0 right-0 px-4 py-2 bg-gray-50 border-t border-gray-200 z-[2000] text-sm">
        <div className="flex items-center gap-6 whitespace-nowrap">
          <div className="flex items-center gap-2">
            <span className={labelCls}>Nuværende uge</span>
            {renderVal(weekStats)}
          </div>
          <div className="flex items-center gap-2">
            <span className={labelCls}>År til dato</span>
            {renderVal(ytdStats)}
          </div>
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={customStart}
              onChange={e => setCustomStart(e.target.value)}
              className="border border-gray-300 rounded px-1 py-0.5 w-32 text-sm"
            />
            <span className="text-gray-400">–</span>
            <input
              type="date"
              value={customEnd}
              onChange={e => setCustomEnd(e.target.value)}
              className="border border-gray-300 rounded px-1 py-0.5 w-32 text-sm"
            />
            {renderVal(customStats)}
          </div>
        </div>
      </div>
    </>
  );
}
